import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  deleteLead,
  exportLeadsCsv,
  fetchKanban,
  fetchLead,
  kanbanQueryFrom,
  leadsQueryFrom,
  listLeads,
  purgeLeads,
  recalculateLead,
  recalculateLeads,
  updateLeadStatus,
} from '@/api/crm';
import { queryKeys } from '@/lib/query-keys';
import type {
  KanbanFiltros,
  LeadFiltros,
  LeadsFiltros,
  PurgaLeadsRequest,
  RecalculoMasivoRequest,
  UpdateLeadStatusRequest,
} from '@/types/crm';

/**
 * CRM del negocio activo (módulo 9).
 *
 * Lo puede leer cualquier miembro (el AGENTE_HUMANO atiende y clasifica conversaciones) y también
 * puede reclasificar un lead a mano o devolverlo al clasificador AUTOMÁTICO; el recálculo MASIVO es
 * solo ADMIN (`requireRole('ADMIN')`, 403 si alguien invoca el endpoint a mano).
 *
 * Cuatro detalles del contrato que se ven acá:
 *  - las query keys incluyen los filtros (se mandan al backend y son parte de la identidad de la
 *    entrada de caché), con `keepPreviousData` para que el tablero no parpadee al cambiar de filtro;
 *  - todas las escrituras invalidan el CRM COMPLETO (`queryKeys.crm.all`): mover un lead lo saca de
 *    una columna y lo mete en otra, así que el kanban, el listado y el detalle dejan de cuadrar;
 *  - además se invalida la bandeja (`queryKeys.conversations.all`): el estado del lead es el mismo
 *    `conversations.estado_lead` que el inbox filtra y pinta en su columna;
 *  - el `PATCH .../estado` devuelve un `LeadCard` SIN el historial ni las señales, así que NO se
 *    escribe en la caché del detalle (se perdería el "por qué" que el diálogo está mostrando): se
 *    invalida, y el detalle abierto se vuelve a pedir.
 */

/** `GET .../crm/kanban` — las 4 columnas del tablero (la lectura principal del CRM). */
export function useKanban(tenantId: string | null, filters: KanbanFiltros = {}) {
  // Los filtros que viajan al backend son los mismos que forman la query key (como en Órdenes).
  const query = kanbanQueryFrom(filters);

  return useQuery({
    queryKey: queryKeys.crm.kanban(tenantId ?? '', query),
    queryFn: () => fetchKanban(tenantId ?? '', filters),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/**
 * `GET .../crm/leads` — listado paginado (`limite` + `offset`) con los totales por estado.
 *
 * Es la vista de tabla: el kanban tiene su propio endpoint y no pagina.
 */
export function useLeads(tenantId: string | null, filters: LeadsFiltros = {}) {
  const query = leadsQueryFrom(filters);

  return useQuery({
    queryKey: queryKeys.crm.leads(tenantId ?? '', query),
    queryFn: () => listLeads(tenantId ?? '', filters),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/**
 * `GET .../crm/leads/:conversationId` — el detalle (señales, clasificación automática, historial,
 * órdenes y últimos mensajes).
 *
 * Se usa para el diálogo de detalle. Con `conversationId` vacío la query no corre: así el diálogo
 * puede montarse cerrado sin disparar una petición.
 */
export function useLead(tenantId: string | null, conversationId: string | null) {
  return useQuery({
    queryKey: queryKeys.crm.lead(tenantId ?? '', conversationId ?? ''),
    queryFn: () => fetchLead(tenantId ?? '', conversationId ?? ''),
    enabled: Boolean(tenantId && conversationId),
  });
}

/** Invalida TODO el CRM (kanban, listado y detalle) + la bandeja del negocio activo. */
function invalidateCrm(queryClient: QueryClient, tenantId: string | null): void {
  if (!tenantId) return;

  queryClient.invalidateQueries({ queryKey: queryKeys.crm.all(tenantId) });
  // El lead ES la conversación (`conversations.estado_lead`): la bandeja del inbox cambia igual.
  queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all(tenantId) });
}

/**
 * `PATCH .../crm/leads/:conversationId/estado` (cualquier miembro) — reclasificación MANUAL.
 *
 * El backend responde 409 si la conversación ya estaba en ese estado Y reclasificada a mano, y 404
 * si el id es de otro negocio; los dos mensajes se muestran tal cual en el diálogo.
 */
export function useUpdateLeadStatus(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      payload,
    }: {
      conversationId: string;
      payload: UpdateLeadStatusRequest;
    }) => updateLeadStatus(tenantId ?? '', conversationId, payload),
    onSuccess: () => invalidateCrm(queryClient, tenantId),
  });
}

/**
 * `POST .../crm/leads/:conversationId/recalcular` (cualquier miembro) — devuelve el lead a la
 * clasificación automática.
 *
 * La respuesta trae `analisis` y `actualizado`, así que el diálogo puede decir qué pasó sin volver a
 * pedir nada; además se invalida el CRM para que el tablero muestre el estado nuevo.
 */
export function useRecalculateLead(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => recalculateLead(tenantId ?? '', conversationId),
    onSuccess: () => invalidateCrm(queryClient, tenantId),
  });
}

/**
 * `POST .../crm/recalcular` (solo ADMIN) — recálculo en lote.
 *
 * Es una operación larga (revisa hasta 2000 conversaciones con sus mensajes) y escribe lo que
 * cambia, así que se invalida el CRM completo al terminar.
 */
export function useRecalculateLeads(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RecalculoMasivoRequest = {}) => recalculateLeads(tenantId ?? '', payload),
    onSuccess: () => invalidateCrm(queryClient, tenantId),
  });
}

/**
 * `GET .../crm/leads/export.csv` (solo ADMIN) — la descarga de la lista de clientes.
 *
 * Es una MUTACIÓN aunque sea un `GET`: el resultado es un ARCHIVO, no un dato con el que se pinte
 * nada, así que no hay nada que cachear y el panel gana `isPending`/`error` como en cualquier otra
 * acción (el botón muestra su spinner y el mensaje del backend sale tal cual). Tampoco invalida
 * nada: descargar no cambia el CRM. El archivo lo guarda la pantalla con `triggerBlobDownload`.
 */
export function useExportLeadsCsv(tenantId: string | null) {
  return useMutation({
    mutationFn: (filters: LeadFiltros = {}) => exportLeadsCsv(tenantId ?? '', filters),
  });
}

/**
 * `DELETE .../crm/leads/:conversationId` (solo ADMIN) — borra UNA conversación.
 *
 * Se invalida el CRM COMPLETO y la bandeja: la conversación desaparece del tablero, del listado y del
 * Inbox, y su detalle pasa a responder 404 (así que el diálogo que estaba abierto se cierra solo).
 */
export function useDeleteLead(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => deleteLead(tenantId ?? '', conversationId),
    onSuccess: () => invalidateCrm(queryClient, tenantId),
  });
}

/**
 * `POST .../crm/purgar` (solo ADMIN) — purga por lotes de conversaciones inactivas.
 *
 * La SIMULACIÓN (`confirmar: false`) no invalida nada a propósito: no cambió ninguna fila y
 * invalidar haría parpadear el tablero sin motivo. Solo se refresca cuando de verdad se borró algo.
 */
export function usePurgeLeads(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PurgaLeadsRequest = {}) => purgeLeads(tenantId ?? '', payload),
    onSuccess: (resultado) => {
      if (resultado.eliminadas > 0) invalidateCrm(queryClient, tenantId);
    },
  });
}

